import {useEffect, useRef, useState} from "react";
import {datadogRum} from "@datadog/browser-rum";
import {App} from "@capacitor/app";

import usePropelAuth from "@/app2/shared/hooks/usePropelAuth";

const isRumReady = (): boolean => {
	const w = (typeof window !== "undefined" ? (window as any) : undefined);
	return w?.__BW_DD_RUM_INITIALIZED__ === true;
};

const getEnvironment = (): "local" | "development" | "qa" | "prod" | "pre-prod" => {
	const API_HOST = process.env.REACT_APP_API_HOST;

	if (API_HOST?.includes("local")) return "local";
	if (API_HOST?.includes("dev")) return "development";
	if (API_HOST?.includes("qa")) return "qa";
	if (API_HOST?.includes("pre-prod")) return "pre-prod";
	return "prod";
};

const trimTrailingSlash = (url: string): string => url.replace(/\/+$/, "");

/** Origins that may receive RUM trace header injection (legacy train API + improve-api). */
const getAllowedTracingUrls = (): string[] | undefined => {
	const urls = [process.env.REACT_APP_API_HOST, process.env.REACT_APP_IMPROVE_API_URL]
		.filter((u): u is string => Boolean(u?.trim()))
		.map(trimTrailingSlash);
	const unique = Array.from(new Set(urls));
	return unique.length > 0 ? unique : undefined;
};

const initDatadogRum = (clientToken: string, version?: string): void => {
	const env = getEnvironment();
	const sessionSampleRate = env === "prod" ? 100 : 10;
	const sessionReplaySampleRate = env === "prod" ? 100 : 0;

	const w = (typeof window !== "undefined" ? (window as any) : undefined);
	if (w?.__BW_DD_RUM_INITIALIZED__ === true) return;
	if (w) w.__BW_DD_RUM_INITIALIZED__ = true;

	datadogRum.init({
		applicationId: "970eccf1-6b6f-436a-a18a-b34d48b82098",
		clientToken,
		// `site` refers to the Datadog site parameter of your organization
		// see https://docs.datadoghq.com/getting_started/site/
		site: "us3.datadoghq.com",
		service: "training-v4-web",
		env,
		sessionSampleRate,
		sessionReplaySampleRate,
		trackUserInteractions: true,
		trackResources: true,
		trackLongTasks: true,
		defaultPrivacyLevel: "mask-user-input",
		allowedTracingUrls: getAllowedTracingUrls(),
		sessionPersistence: "local-storage",
		version
	});
};

const useDatadog = (): void => {
	const clientToken = process.env.REACT_APP_DATA_DOG_CLIENT_TOKEN;
	const hasAttemptedInit = useRef(false);
	const [rumReady, setRumReady] = useState(isRumReady());

	const {authInfo, authUser} = usePropelAuth();
	const org = authInfo.orgHelper?.getOrgs()?.[0];

	useEffect(() => {
		if (!clientToken) return;
		if (hasAttemptedInit.current) return;
		if (rumReady) return;

		hasAttemptedInit.current = true;

		App.getInfo()
			.then(({version}) => initDatadogRum(clientToken, version))
			.catch(() => initDatadogRum(clientToken))
			.finally(() => setRumReady(true));
	}, [clientToken, rumReady]);

	useEffect(() => {
		if (!clientToken) return;
		if (!authUser) return;
		if (!rumReady) return;

		const roles = !org
			? []
			: [
					...org.userInheritedRolesPlusCurrentRole,
					...org.userAssignedAdditionalRoles
				];

		datadogRum.setUser({
			id: authUser.userId,
			name: `${authUser.firstName ?? ""} ${authUser.lastName ?? ""}`.trim(),
			email: authUser.email,
			organizationName: org?.orgName,
			organizationId: org?.orgId,
			roles
		});
	}, [clientToken, authUser, org, rumReady]);
};

export default useDatadog;
